import gql from 'graphql-tag';
import { useQuery, provideApolloClient } from '@vue/apollo-composable';
import { watch, computed, ref } from 'vue';
import useAlerts from './useAlerts';
import apolloClient from './useApolloClient';

const { addAlert } = useAlerts();

const GET_COMPANY_CONTACTS = gql`
  query CompanyQuery($companyId: ID!) {
    company(id: $companyId) {
      NAME
      contacts {
        FIRSTNAME
        LASTNAME
        CONTACTID
        TITLES
        DEPARTMENTS
        NA
        ISPRIMARY
        INACTIVE
        email {
          ADDRESS
        }
        address {
          LINE1
          LINE2
          CITY
          STATE
          POSTALCODE
          COUNTRYNAME
        }
        phones {
          NUMBERDISPLAY
          TYPEID
        }
      }
    }
  }
`;

const GET_OTHER_CONTACTS = gql`
  query OtherLinkedContacts($companyId: ID!) {
    otherLinkedContacts(companyId: $companyId) {
      FIRSTNAME
      LASTNAME
      CONTACTID
      TITLES
      DEPARTMENTS
      NA
      INACTIVE
      company {
        NAME
        COMPANYID
      }
      email {
        ADDRESS
      }
      address {
        LINE1
        LINE2
        CITY
        STATE
        POSTALCODE
        COUNTRYNAME
      }
      phones {
        NUMBERDISPLAY
        TYPEID
      }
    }
  }
`;

const GET_OTHER_COMPANIES = gql`
  query OtherLinkedCompanies($companyId: ID!) {
    otherLinkedCompanies(companyId: $companyId) {
      NAME
      COMPANYID
      address {
        LINE1
        LINE2
        CITY
        STATE
        POSTALCODE
        COUNTRYNAME
      }
      phones {
        NUMBERDISPLAY
        TYPEID
      }
    }
  }
`;

export default function useCompanyContactsQuery(companyId) {
  provideApolloClient(apolloClient);

  // Execute all three queries
  const companyQuery = useQuery(GET_COMPANY_CONTACTS, { companyId }, { fetchPolicy: 'no-cache' });
  const otherContactsQuery = useQuery(GET_OTHER_CONTACTS, { companyId }, { fetchPolicy: 'no-cache' });
  const otherCompaniesQuery = useQuery(GET_OTHER_COMPANIES, { companyId }, { fetchPolicy: 'no-cache' });

  // Combined loading state
  const loading = computed(() => companyQuery.loading.value || otherContactsQuery.loading.value || otherCompaniesQuery.loading.value);

  // Combined error state
  const error = computed(() => companyQuery.error.value || otherContactsQuery.error.value || otherCompaniesQuery.error.value);

  // Company name from the main query
  const companyName = computed(() => companyQuery.result.value?.company?.NAME || '');

  // Combined contacts list
  const allContacts = computed(() => {
    const contacts = [];

    // Add main company contacts
    if (companyQuery.result.value?.company?.contacts) {
      const mainCompanyContacts = companyQuery.result.value.company.contacts.map((contact) => ({
        ...contact,
        company: {
          NAME: companyQuery.result.value.company.NAME,
          COMPANYID: companyId,
        },
        isMainCompany: true,
      }));
      contacts.push(...mainCompanyContacts);
    }

    // Add other linked contacts
    if (otherContactsQuery.result.value?.otherLinkedContacts) {
      const otherContacts = otherContactsQuery.result.value.otherLinkedContacts.map((contact) => ({
        ...contact,
        isMainCompany: false,
      }));
      contacts.push(...otherContacts);
    }

    // Add other companies as contact-like entries
    if (otherCompaniesQuery.result.value?.otherLinkedCompanies) {
      const companyEntries = otherCompaniesQuery.result.value.otherLinkedCompanies.map((company) => ({
        FIRSTNAME: '',
        LASTNAME: '',
        CONTACTID: `company_${company.COMPANYID}`,
        TITLES: [],
        DEPARTMENTS: [],
        NA: false,
        ISPRIMARY: false,
        INACTIVE: false,
        email: null,
        address: company.address,
        phones: company.phones,
        company: {
          NAME: company.NAME,
          COMPANYID: company.COMPANYID,
        },
        isMainCompany: false,
        isCompanyEntry: true,
      }));
      contacts.push(...companyEntries);
    }

    return contacts;
  });

  // Combined result object
  const result = computed(() => ({
    company: {
      NAME: companyName.value,
      contacts: allContacts.value,
    },
    allContacts: allContacts.value,
    companyName: companyName.value,
  }));

  // Refetch function for all queries
  const refetch = async (variables) => {
    await Promise.all([companyQuery.refetch(variables), otherContactsQuery.refetch(variables), otherCompaniesQuery.refetch(variables)]);
  };

  // Watch for errors
  watch(error, () => {
    if (error.value) {
      console.error('Error fetching company contacts:', error.value);
      addAlert('Error fetching company contacts', 'high', 5);
    }
  });

  return {
    result,
    loading,
    error,
    refetch,
    companyName,
    allContacts,
  };
}
